import React, { useState, useRef, useEffect } from 'react';
import { Plus, Edit2, Trash2, Minus, Upload, Download, AlertTriangle, CheckCircle2, XCircle, Tag, Layers, Eye, X } from 'lucide-react';
import { Plus as PlusIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import { useFetch } from '../hooks';
import { medicineService, categoryService, brandService } from '../services/api';
import { getStatusColor, formatCurrency } from '../utils/helpers';

const Toggle = ({ value, onChange, label }) => (
  <button type="button" onClick={() => onChange(!value)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${value ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
    aria-label={label}>
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

/* ── Category & Brand Manager Modal ────────────────────────────────────── */
const ItemManagerModal = ({ title, items, onAdd, onToggle, onClose }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [name, setName]   = useState('');
  const [image, setImage] = useState(null);
  const [active, setActive] = useState(true);
  const [preview, setPreview] = useState(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleAdd = () => {
    const val = name.trim();
    if (!val) { toast.error('Name is required'); return; }
    if (items.find(i => i.name.toLowerCase() === val.toLowerCase())) {
      toast.error('Already exists'); return;
    }
    onAdd({ name: val, image, active });
    setName(''); setImage(null); setPreview(null); setActive(true);
    setShowAddForm(false);
    toast.success(`${val} added!`);
  };

  return (
    <Modal isOpen onClose={onClose} title={title} size="md">
      <div className="space-y-4">

        {/* Add New Button — only show if onAdd is provided */}
        {!showAddForm && onAdd && (
          <button onClick={() => setShowAddForm(true)}
            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-medihub-primary hover:text-medihub-primary transition-colors">
            <PlusIcon className="w-4 h-4" /> Add New
          </button>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl space-y-3 bg-gray-50 dark:bg-gray-700/30">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Add New</p>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name <span className="text-red-500">*</span></label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                className="input-field"
                placeholder="Enter name..."
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Image</label>
              <div className="flex items-center gap-3">
                {preview
                  ? <img src={preview} alt="preview" className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600" />
                  : <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-gray-400"><Upload className="w-5 h-5" /></div>
                }
                <input type="file" accept="image/*" className="input-field flex-1 text-xs" onChange={handleImageChange} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{active ? 'Active' : 'Inactive'}</span>
                <Toggle value={active} onChange={setActive} label="status" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => { setShowAddForm(false); setName(''); setImage(null); setPreview(null); setActive(true); }} className="btn-secondary flex-1 text-sm">Cancel</button>
              <button onClick={handleAdd} className="btn-primary flex-1 text-sm">Add</button>
            </div>
          </div>
        )}

        {/* Items List */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No items yet</p>}
          {items.map(item => (
            <div key={item.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center gap-3">
                {item.image
                  ? <img src={typeof item.image === 'string' ? item.image : URL.createObjectURL(item.image)} alt={item.name} className="w-8 h-8 rounded-lg object-cover" />
                  : <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-400">{item.name[0]}</div>
                }
                <span className={`text-sm font-medium ${item.active ? 'text-gray-900 dark:text-white' : 'text-gray-400 line-through'}`}>{item.name}</span>
              </div>
              <Toggle value={item.active} onChange={() => onToggle(item.id)} label="toggle" />
            </div>
          ))}
        </div>

        <button onClick={onClose} className="btn-secondary w-full">Close</button>
      </div>
    </Modal>
  );
};

const MedicineForm = ({ initial, onSubmit, onClose, categories, brands }) => {
  const [medicineImages, setMedicineImages] = useState([]);
  const rules = { name: { required: true }, price: { required: true }, stock: { required: true } };
  const { values, errors, handleChange, handleBlur, handleSubmit, setValues } = useForm(
    initial ? {
      name: initial.name || '', brand: initial.brand || '', description: initial.description || '',
      price: String(initial.price || ''), discounted_price: String(initial.discounted_price || ''),
      discount_percent: String(initial.discount_percent || ''), category_id: String(initial.category_id || ''),
      stock: String(initial.stock || ''), requires_prescription: initial.requires_prescription ?? 0,
      status: initial.status || 'active', image_url: null,
      generic_name: initial.generic_name || '', composition: initial.composition || '',
      dosage: initial.dosage || '', batch_number: initial.batch_number || '',
      expiry_date: initial.expiry_date || '',
    } : {
      name: '', brand: '', description: '', price: '', discounted_price: '',
      discount_percent: '', category_id: '', stock: '',
      requires_prescription: 0, status: 'active', image_url: null,
      generic_name: '', composition: '', dosage: '', batch_number: '', expiry_date: '',
    },
    rules
  );

  const activeCategories = categories.filter(c => c.active);
  const activeBrands     = brands.filter(b => b.active);

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setMedicineImages(prev => [...prev, ...files]);
  };

  const removeImage = (idx) => setMedicineImages(prev => prev.filter((_, i) => i !== idx));

  return (
    <Form onSubmit={handleSubmit(vals => onSubmit({ ...vals, medicine_images: medicineImages }))}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Medicine Name" error={errors.name} required>
          <input name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="Paracetamol 500mg" />
        </FormField>
        <FormField label="Generic Name">
          <input name="generic_name" value={values.generic_name} onChange={handleChange} className="input-field" placeholder="Acetaminophen" />
        </FormField>
        <FormField label="Brand">
          <select name="brand" value={values.brand} onChange={handleChange} className="input-field">
            <option value="">Select brand</option>
            {activeBrands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </FormField>
        <FormField label="Category">
          <select name="category_id" value={values.category_id} onChange={handleChange} className="input-field">
            <option value="">Select category</option>
            {activeCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </FormField>
        <FormField label="Price" error={errors.price} required>
          <input name="price" type="number" step="0.01" value={values.price} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="500" />
        </FormField>
        <FormField label="Discounted Price">
          <input name="discounted_price" type="number" readOnly step="0.01" value={values.discounted_price} onChange={handleChange} className="input-field" placeholder="450" />
        </FormField>
        <FormField label="Discount (%)">
          <input name="discount_percent" type="number" step="0.01" value={values.discount_percent} onChange={e => {
            handleChange(e);
            const price = parseFloat(values.price);
            const disc  = parseFloat(e.target.value);
            if (price > 0 && disc > 0) setValues(p => ({ ...p, discounted_price: (price - price * disc / 100).toFixed(2) }));
          }} className="input-field" placeholder="10" />
        </FormField>
        <FormField label="Stock" error={errors.stock} required>
          <input name="stock" type="number" value={values.stock} onChange={handleChange} onBlur={handleBlur} className="input-field" placeholder="100" />
        </FormField>
        <FormField label="Dosage">
          <input name="dosage" value={values.dosage} onChange={handleChange} className="input-field" placeholder="500mg twice daily" />
        </FormField>
        <FormField label="Batch Number">
          <input name="batch_number" value={values.batch_number} onChange={handleChange} className="input-field" placeholder="BATCH-2024-001" />
        </FormField>
        <FormField label="Expiry Date">
          <input name="expiry_date" type="date" value={values.expiry_date} onChange={handleChange} className="input-field" />
        </FormField>
        <FormField label="Status">
          <select name="status" value={values.status} onChange={handleChange} className="input-field">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
      </div>

      <FormField label="Composition">
        <textarea name="composition" value={values.composition} onChange={handleChange} className="input-field resize-none" rows={2} placeholder="Each tablet contains..." />
      </FormField>
      <FormField label="Description">
        <textarea name="description" value={values.description} onChange={handleChange} className="input-field resize-none" rows={2} placeholder="Medicine description..." />
      </FormField>

      <div className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Prescription Required</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Requires doctor's prescription</p>
        </div>
        <Toggle
          value={values.requires_prescription === 1 || values.requires_prescription === true}
          onChange={v => setValues(prev => ({ ...prev, requires_prescription: v ? 1 : 0 }))}
          label="Prescription Required"
        />
      </div>

      <FormField label="Main Image">
        <input type="file" accept="image/*" className="input-field"
          onChange={e => setValues(p => ({ ...p, image_url: e.target.files[0] || null }))} />
      </FormField>

      <FormField label="Additional Images">
        <label className="flex items-center gap-2 cursor-pointer input-field py-2 text-sm text-gray-500 dark:text-gray-400 hover:border-medihub-primary transition-colors">
          <Upload className="w-4 h-4 shrink-0 text-gray-400" />
          <span>Select images…</span>
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleImagesChange} />
        </label>
        {medicineImages.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {medicineImages.map((file, idx) => (
              <div key={idx} className="relative group">
                <img src={URL.createObjectURL(file)} alt="" className="w-16 h-16 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
                <button type="button" onClick={() => removeImage(idx)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-xs leading-none">×</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </FormField>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">{initial ? 'Update' : 'Add Medicine'}</button>
      </div>
    </Form>
  );
};

 

/* ── CSV Template columns & helpers ──────────────────────────────────────────── */
const CSV_HEADERS = ['name','price','discounted_price','stock','category_id','brand','description','discount_percent','requires_prescription','status','generic_name','composition','dosage','batch_number','expiry_date'];
const CSV_EXAMPLE = ['Paracetamol 500mg','100','90','50','1','PharmaCo','Pain reliever','10','0','active','Acetaminophen','Each tablet 500mg','500mg twice daily','BATCH-001','2026-12-31'];
// Note: CSV has 15 headers and 15 example values — must match exactly

const downloadTemplate = async () => {
  try { await medicineService.downloadTemplate(); }
  catch { toast.error('Failed to download template'); }
};

const parseCSV = (text, existingNames) => {
  const lines = text.trim().split('\n').filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  return lines.slice(1).map((line, idx) => {
    const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    const row  = {};
    headers.forEach((h, i) => { row[h] = vals[i] ?? ''; });
    const errors = [];
    if (!row.name)                                         errors.push('Name is required');
    if (!row.price || isNaN(+row.price) || +row.price < 0) errors.push('Invalid price');
    if (!row.discounted_price || isNaN(+row.discounted_price)) errors.push('discounted_price is required');
    if (!row.stock || isNaN(+row.stock) || +row.stock < 0) errors.push('Invalid stock');
    if (row.expiry_date && new Date(row.expiry_date) < new Date()) errors.push('Expiry date is in the past');
    if (existingNames.includes(row.name?.toLowerCase()))   errors.push('Duplicate medicine name');
    return {
      _row:    idx + 2,
      _errors: errors,
      _valid:  errors.length === 0,
      name:                 row.name                || '',
      price:                row.price               || '',
      discounted_price:     row.discounted_price    || '',
      stock:                row.stock               || '',
      category_id:          row.category_id         || '',
      brand:                row.brand               || '',
      description:          row.description         || '',
      discount_percent:     row.discount_percent    || '',
      requires_prescription: row.requires_prescription === '1' ? 1 : 0,
      status:               row.status              || 'active',
      generic_name:         row.generic_name        || '',
      composition:          row.composition         || '',
      dosage:               row.dosage              || '',
      batch_number:         row.batch_number        || '',
      expiry_date:          row.expiry_date         || '',
    };
  });
};

/* ── Bulk Upload Modal ────────────────────────────────────────────────────────── */
const BulkUploadModal = ({ existingMedicines, onClose, onSuccess }) => {
  const fileRef               = useRef();
  const [step, setStep]        = useState(1);
  const [file, setFile]        = useState(null);
  const [rows, setRows]        = useState([]);
  const [loading, setLoading]  = useState(false);
  const [fileName, setFileName] = useState('');

  const existingNames = existingMedicines?.map(m => m.name.toLowerCase()) || [];

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) { toast.error('Please upload a .csv file'); return; }
    setFile(f);
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result, existingNames);
      if (!parsed.length) { toast.error('No data rows found in CSV'); return; }
      setRows(parsed);
      setStep(2);
    };
    reader.readAsText(f);
  };

  const validRows   = rows.filter(r => r._valid);
  const invalidRows = rows.filter(r => !r._valid);

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const res = await medicineService.bulkUpload(file);
      toast.success(res?.message || `Medicines imported successfully!`);
      setStep(3);
      onSuccess();
    } catch (err) { toast.error(err?.response?.data?.message || 'Import failed'); }
    finally { setLoading(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title="Bulk Upload Medicines" size="xl">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {['Upload CSV', 'Preview & Validate', 'Done'].map((label, i) => (
          <React.Fragment key={label}>
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step > i + 1 ? 'bg-green-500 text-white'
                : step === i + 1 ? 'bg-medihub-primary text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
              }`}>{step > i + 1 ? '✓' : i + 1}</div>
              <span className={`text-xs font-medium ${
                step === i + 1 ? 'text-gray-900 dark:text-white' : 'text-gray-400'
              }`}>{label}</span>
            </div>
            {i < 2 && <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1 — Upload */}
      {step === 1 && (
        <div className="space-y-5">
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
            <Download className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Step 1: Download the CSV template</p>
              <p className="text-xs text-gray-500 mt-0.5">Fill in your medicine data using the provided template format.</p>
              <button onClick={downloadTemplate} className="mt-2 flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors">
                <Download className="w-3.5 h-3.5" />Download Template
              </button>
            </div>
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-10 text-center cursor-pointer hover:border-medihub-primary dark:hover:border-medihub-highlight transition-colors"
          >
            <Upload className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Click to upload CSV file</p>
            <p className="text-xs text-gray-400 mt-1">Only .csv files are supported</p>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
          </div>

          <div className="p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl">
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Expected CSV columns:</p>
            <p className="text-xs font-mono text-gray-500 dark:text-gray-400">{CSV_HEADERS.join(', ')}</p>
          </div>
        </div>
      )}

      {/* Step 2 — Preview */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700/40 rounded-xl text-center">
              <p className="text-xl font-bold text-gray-900 dark:text-white">{rows.length}</p>
              <p className="text-xs text-gray-500">Total Rows</p>
            </div>
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl text-center">
              <p className="text-xl font-bold text-green-600 dark:text-green-400">{validRows.length}</p>
              <p className="text-xs text-gray-500">Valid</p>
            </div>
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-center">
              <p className="text-xl font-bold text-red-600 dark:text-red-400">{invalidRows.length}</p>
              <p className="text-xs text-gray-500">Errors</p>
            </div>
          </div>

          {/* Preview table */}
          <div className="overflow-x-auto max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
                <tr>
                  {['Row', 'Name', 'Price', 'Disc. Price', 'Stock', 'Category', 'Brand', 'Status', 'Errors'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row) => (
                  <tr key={row._row} className={row._valid ? '' : 'bg-red-50 dark:bg-red-900/10'}>
                    <td className="px-3 py-2 font-mono">
                      <div className="flex items-center gap-1">
                        {row._valid
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                          : <XCircle      className="w-3.5 h-3.5 text-red-500"   />}
                        {row._row}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{row.name || <span className="text-red-400">—</span>}</td>
                    <td className="px-3 py-2">{row.price}</td>
                    <td className="px-3 py-2">{row.discounted_price || '—'}</td>
                    <td className="px-3 py-2">{row.stock}</td>
                    <td className="px-3 py-2">{row.category_id || '—'}</td>
                    <td className="px-3 py-2">{row.brand || '—'}</td>
                    <td className="px-3 py-2">{row.status}</td>
                    <td className="px-3 py-2">
                      {row._valid
                        ? <span className="text-green-600 text-xs">OK</span>
                        : <div className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 text-red-500 shrink-0 mt-0.5" /><span className="text-red-500 text-xs">{row._errors.join('; ')}</span></div>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between gap-3 pt-1">
            <button onClick={() => { setStep(1); setRows([]); setFile(null); setFileName(''); if (fileRef.current) fileRef.current.value = ''; }} className="btn-secondary">
              ← Re-upload
            </button>
            <button
              onClick={handleImport}
              disabled={loading || !file}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {loading ? 'Importing…' : `Import ${fileName}`}
            </button>
          </div>
        </div>
      )}

      {/* Step 3 — Done */}
      {step === 3 && (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{validRows.length} Medicine(s) Imported!</p>
            {invalidRows.length > 0 && (
              <p className="text-sm text-orange-500 mt-1">{invalidRows.length} row(s) were skipped due to errors.</p>
            )}
          </div>
          <button onClick={onClose} className="btn-primary">Done</button>
        </div>
      )}
    </Modal>
  );
};

/* ── Medicine Detail Modal ──────────────────────────────────────────────── */
const MedicineDetailModal = ({ medicine, onEdit, onClose }) => {
  const [activeImg, setActiveImg] = useState(medicine.image_url || null);
  const allImages = [
    ...(medicine.image_url ? [medicine.image_url] : []),
    ...(medicine.images || []),
  ].filter((v, i, a) => a.indexOf(v) === i);

  const INFO = [
    { label: 'Generic Name',   value: medicine.generic_name },
    { label: 'Brand',          value: medicine.brand },
    { label: 'Category',       value: medicine.category_name },
    { label: 'Composition',    value: medicine.composition },
    { label: 'Dosage',         value: medicine.dosage },
    { label: 'Batch Number',   value: medicine.batch_number },
    { label: 'Expiry Date',    value: medicine.expiry_date },
    { label: 'Stock',          value: medicine.stock },
    { label: 'Price',          value: medicine.price ? formatCurrency(medicine.price) : null },
    { label: 'Discounted Price', value: medicine.discounted_price ? formatCurrency(medicine.discounted_price) : null },
    { label: 'Discount',       value: medicine.discount_percent ? `${medicine.discount_percent}%` : null },
    { label: 'Prescription',   value: medicine.requires_prescription ? 'Required' : 'Not Required' },
    { label: 'Status',         value: medicine.status },
  ];

  return (
    <Modal isOpen onClose={onClose} title="Medicine Details" size="lg">
      <div className="space-y-4">
        {/* Images */}
        {allImages.length > 0 && (
          <div className="space-y-2">
            <img src={activeImg || allImages[0]} alt={medicine.name}
              className="w-full h-48 object-contain rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800" />
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {allImages.map((url, i) => (
                  <img key={i} src={url} alt="" onClick={() => setActiveImg(url)}
                    className={`w-14 h-14 rounded-lg object-cover shrink-0 cursor-pointer border-2 transition-colors ${
                      (activeImg || allImages[0]) === url ? 'border-medihub-primary' : 'border-transparent'
                    }`} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Name + Description */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">{medicine.name}</h3>
          {medicine.description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{medicine.description}</p>}
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {INFO.filter(i => i.value != null && i.value !== '').map(({ label, value }) => (
            <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
              <p className="text-xs text-gray-400 dark:text-gray-500">{label}</p>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mt-0.5 capitalize">{value}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="btn-secondary">Close</button>
          <button onClick={onEdit} className="btn-primary flex items-center gap-1.5"><Edit2 className="w-4 h-4" />Edit</button>
        </div>
      </div>
    </Modal>
  );
};

const Inventory = () => {
  // const { data: medicines, loading, refetch } = useFetch(() => medicineService.getAll());
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
  const {
    data: medicinesData,
    loading,
    refetch,
  } = useFetch(
    () => medicineService.getAll({ page: currentPage, limit: 10 }),
    [currentPage], // currentPage change hone par re-fetch
  );
  console.log("medicine data:", medicinesData);
  const medicines = medicinesData?.medicines || [];
  const pagination = medicinesData?.pagination || {};
  const { data: apiCategories, refetch: refetchCategories } = useFetch(() => categoryService.getAll());
  const { data: apiBrands,     refetch: refetchBrands     } = useFetch(() => brandService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [stockQty, setStockQty] = useState(0);
  const [showBulk, setShowBulk] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showCatManager,   setShowCatManager]   = useState(false);
  const [showBrandManager, setShowBrandManager] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);
  const [filters, setFilters] = useState({ search: '', category: '', brand: '', expiry: '' });

  const onFilterChange = (key, val) => setFilters(p => ({ ...p, [key]: val }));
  const onFilterReset  = () => setFilters({ search: '', category: '', brand: '', expiry: '' });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;



  const filtered = (medicines || []).filter(m => {
    const today = new Date(); today.setHours(0,0,0,0);
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (![m.name, m.brand, m.category_name, m.generic_name].join(' ').toLowerCase().includes(q)) return false;
    }
    if (filters.category && String(m.category_id) !== String(filters.category)) return false;
    if (filters.brand    && m.brand !== filters.brand) return false;
    if (filters.expiry) {
      if (!m.expiry_date) return filters.expiry === 'none';
      const diff = Math.ceil((new Date(m.expiry_date) - today) / 86400000);
      if (filters.expiry === 'expired' && diff >= 0) return false;
      if (filters.expiry === '30'      && !(diff >= 0 && diff <= 30))  return false;
      if (filters.expiry === '90'      && !(diff > 30 && diff <= 90))  return false;
      if (filters.expiry === 'good'    && diff <= 90) return false;
      if (filters.expiry === 'none'    && m.expiry_date) return false;
    }
    return true;
  });

  const categories = (apiCategories || []).map(c => ({
    id: c.id,
    name: c.name,
    image: c.image || c.image_url || null,
    active: c.status === 'active',
  }));

  const addCategory = async (item) => {
    try {
      const res = await categoryService.create(item.name);
      toast.success(res?.message || `${item.name} added!`);
      refetchCategories();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to add category'); }
  };
  const toggleCategory = async (id) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    const newStatus = cat.active ? "inactive" : "active";
    try {
      await categoryService.toggleStatus(id, newStatus);
      refetchCategories();
    } catch {
      toast.error("Failed to update category");
    }
  };

  const brands = (apiBrands || []).map(b => ({
    id: b.id,
    name: b.name,
    image: b.image || b.image_url || null,
    active: b.status === 'active',
  }));

  const addBrand = async (item) => {
    try {
      const res = await brandService.create(item.name);
      toast.success(res?.message || `${item.name} added!`);
      refetchBrands();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to add brand'); }
  };

  const toggleBrand = async (id) => {
    const brand = brands.find(b => b.id === id);
    if (!brand) return;
    const newStatus = brand.active ? 'inactive' : 'active';
    try {
      await brandService.toggleStatus(id, newStatus);
      refetchBrands();
    } catch { toast.error('Failed to update brand'); }
  };

  const handleSave = async (values) => {
    try {
      if (modal === 'edit') {
        const res = await medicineService.update(selected.id, values);
        toast.success(res?.message || 'Medicine updated!');
      } else {
        const res = await medicineService.create(values);
        toast.success(res?.message || 'Medicine added!');
      }
      refetch(); setModal(null);
    } catch (err) { toast.error(err?.response?.data?.message || 'Something went wrong'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await medicineService.delete(deleteTarget.id);
      toast.success(res?.message || 'Medicine deleted');
      setDeleteTarget(null);
      refetch();
    } catch (err) { toast.error(err?.response?.data?.message || 'Failed to delete'); }
    finally { setDeleting(false); }
  };

  const handleStock = async () => {
    try { await medicineService.updateStock(selected.id, stockQty); toast.success('Stock updated!'); refetch(); setModal(null); }
    catch { toast.error('Failed to update stock'); }
  };

  const columns = [
    {
      key: 'name', label: 'Medicine', render: (v, row) => (
        <div className="flex items-center gap-3">
          {row.image_url
            ? <img src={row.image_url} alt={v} className="w-8 h-8 rounded-lg object-cover shrink-0" />
            : <div className="w-8 h-8 rounded-lg bg-medihub-light dark:bg-medihub-dark/40 flex items-center justify-center text-xs font-bold text-medihub-secondary shrink-0">{v[0]}</div>
          }
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{v}</p>
            <p className="text-xs text-gray-500">{row.brand || '—'}</p>
          </div>
        </div>
      )
    },
    { key: 'category_name', label: 'Category', render: v => v || '—' },
    { key: 'price', label: 'Price', render: v => formatCurrency(v) },
    { key: 'discounted_price', label: 'Disc. Price', render: v => v ? formatCurrency(v) : '—' },
    { key: 'stock', label: 'Stock', render: v => <span className="font-medium">{v}</span> },
    {
      key: 'expiry_date', label: 'Expiry', render: v => {
        if (!v) return <span className="text-gray-400 text-xs">—</span>;
        const today = new Date(); today.setHours(0,0,0,0);
        const exp   = new Date(v);
        const diff  = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
        if (diff < 0)  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"><AlertTriangle className="w-3 h-3" />Expired</span>;
        if (diff <= 30) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"><AlertTriangle className="w-3 h-3" />{diff}d left</span>;
        if (diff <= 90) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300">{v}</span>;
        return <span className="text-xs text-gray-500 dark:text-gray-400">{v}</span>;
      }
    },
    {
      key: 'requires_prescription', label: 'Rx Required', render: v => (
        <span className={`badge ${v ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
          {v ? 'Yes' : 'No'}
        </span>
      )
    },
    { key: 'status', label: 'Status', render: v => <span className={`badge ${getStatusColor(v)}`}>{v}</span> },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => setViewTarget(row)} className="p-1.5 hover:bg-medihub-light dark:hover:bg-medihub-dark/20 rounded-lg transition-colors text-medihub-primary" title="View"><Eye className="w-4 h-4" /></button>
          <button onClick={() => { setSelected(row); setStockQty(0); setModal('stock'); }} className="text-xs px-2 py-1 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 transition-colors">Stock</button>
          <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500"><Edit2 className="w-4 h-4" /></button>
          <button onClick={() => setDeleteTarget(row)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500"><Trash2 className="w-4 h-4" /></button>
        </div>
      )
    },
  ];

  useEffect(() => {
  if (pagination.total_pages) {
    setTotalPages(pagination.total_pages);
    setTotalItems(pagination.total);
  }
}, [pagination.total_pages]);


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Medicine Inventory
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage medicines and stock levels
        </p>
      </div>

      {/* Expiry Summary Cards */}
      {(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expired = (medicines || []).filter(
          (m) => m.expiry_date && new Date(m.expiry_date) < today,
        ).length;
        const soon30 = (medicines || []).filter((m) => {
          if (!m.expiry_date) return false;
          const d = Math.ceil((new Date(m.expiry_date) - today) / 86400000);
          return d >= 0 && d <= 30;
        }).length;
        const soon90 = (medicines || []).filter((m) => {
          if (!m.expiry_date) return false;
          const d = Math.ceil((new Date(m.expiry_date) - today) / 86400000);
          return d > 30 && d <= 90;
        }).length;
        const good = (medicines || []).filter((m) => {
          if (!m.expiry_date) return false;
          const d = Math.ceil((new Date(m.expiry_date) - today) / 86400000);
          return d > 90;
        }).length;
        return (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: "Expired",
                value: expired,
                color: "text-red-700 dark:text-red-400",
                bg: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800",
                dot: "bg-red-500",
                desc: "Already expired",
              },
              {
                label: "Expiring ≤30d",
                value: soon30,
                color: "text-orange-700 dark:text-orange-400",
                bg: "bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800",
                dot: "bg-orange-500",
                desc: "Critical — act now",
              },
              {
                label: "Expiring ≤90d",
                value: soon90,
                color: "text-yellow-700 dark:text-yellow-400",
                bg: "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800",
                dot: "bg-yellow-500",
                desc: "Monitor closely",
              },
              {
                label: "Good Stock",
                value: good,
                color: "text-green-700 dark:text-green-400",
                bg: "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800",
                dot: "bg-green-500",
                desc: "More than 90 days",
              },
            ].map(({ label, value, color, bg, dot, desc }) => (
              <div key={label} className={`card p-4 rounded-xl ${bg}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`w-2.5 h-2.5 rounded-full shrink-0 ${dot}`}
                  />
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    {label}
                  </p>
                </div>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Filter Bar */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <input
            value={filters.search}
            onChange={(e) => onFilterChange("search", e.target.value)}
            placeholder="Search by name, brand, generic…"
            className="input-field flex-1 min-w-[180px]"
          />
          {/* Category */}
          <select
            value={filters.category}
            onChange={(e) => onFilterChange("category", e.target.value)}
            className="input-field w-auto"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {/* Brand */}
          <select
            value={filters.brand}
            onChange={(e) => onFilterChange("brand", e.target.value)}
            className="input-field w-auto"
          >
            <option value="">All Brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>
          {/* Expiry */}
          <select
            value={filters.expiry}
            onChange={(e) => onFilterChange("expiry", e.target.value)}
            className="input-field w-auto"
          >
            <option value="">All Expiry</option>
            <option value="expired">🔴 Expired</option>
            <option value="30">🟠 Expiring ≤30 days</option>
            <option value="90">🟡 Expiring ≤90 days</option>
            <option value="good">🟢 Good (&gt;90 days)</option>
            <option value="none">⚪ No Expiry Date</option>
          </select>
          {/* Reset */}
          {activeFilterCount > 0 && (
            <button
              onClick={onFilterReset}
              className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 transition-colors whitespace-nowrap"
            >
              <X className="w-4 h-4" />
              Reset ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      <Table
        title={`Medicines (${filtered.length}${activeFilterCount ? ` of ${medicines?.length || 0}` : ""})`}
        columns={columns}
        data={filtered}
        loading={loading}
        searchKeys={["name", "category", "manufacturer"]}
        exportFilename="inventory"
        serverPagination={{
          page: currentPage,
          totalPages: totalPages,
          total: totalItems,
          perPage: 10,
          onPageChange: setCurrentPage,
        }}
        actions={
          <div className="overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              <button
                onClick={() => setShowCatManager(true)}
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
              >
                <Layers className="w-4 h-4" />
                Categories
              </button>
              <button
                onClick={() => setShowBrandManager(true)}
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
              >
                <Tag className="w-4 h-4" />
                Brands
              </button>
              <button
                onClick={() => setShowBulk(true)}
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg border border-medihub-primary text-medihub-secondary bg-medihub-light hover:bg-medihub-soft dark:bg-medihub-dark/20 dark:border-medihub-accent dark:text-medihub-highlight dark:hover:bg-medihub-dark/40 transition-colors whitespace-nowrap"
              >
                <Upload className="w-4 h-4" />
                Bulk Upload
              </button>
              <button
                onClick={() => {
                  setSelected(null);
                  setModal("add");
                }}
                className="btn-primary flex items-center gap-1.5 whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                Add Medicine
              </button>
            </div>
          </div>
        }
      />

      {showBulk && (
        <BulkUploadModal
          existingMedicines={medicines || []}
          onClose={() => setShowBulk(false)}
          onSuccess={refetch}
        />
      )}

      {viewTarget && (
        <MedicineDetailModal
          medicine={viewTarget}
          onEdit={() => {
            setSelected(viewTarget);
            setViewTarget(null);
            setModal("edit");
          }}
          onClose={() => setViewTarget(null)}
        />
      )}

      <Modal
        isOpen={modal === "add" || modal === "edit"}
        onClose={() => setModal(null)}
        title={modal === "edit" ? "Edit Medicine" : "Add Medicine"}
        size="lg"
      >
        <MedicineForm
          initial={modal === "edit" ? selected : null}
          onSubmit={handleSave}
          onClose={() => setModal(null)}
          categories={categories}
          brands={brands}
        />
      </Modal>

      {showCatManager && (
        <ItemManagerModal
          title="Manage Categories"
          items={categories}
          onAdd={addCategory}
          onToggle={toggleCategory}
          onClose={() => setShowCatManager(false)}
        />
      )}

      {showBrandManager && (
        <ItemManagerModal
          title="Manage Brands"
          items={brands}
          onAdd={addBrand}
          onToggle={toggleBrand}
          onClose={() => setShowBrandManager(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Medicine"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-gray-900 dark:text-white">
              {deleteTarget?.name}
            </span>
            ? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteTarget(null)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modal === "stock"}
        onClose={() => setModal(null)}
        title="Manage Stock"
        size="sm"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm">
            <p className="font-medium text-gray-900 dark:text-white">
              {selected?.name}
            </p>
            <p className="text-gray-500 mt-0.5">
              Current stock:{" "}
              <strong>
                {selected?.stock} {selected?.unit}
              </strong>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setStockQty((q) => q - 10)}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Minus className="w-4 h-4" />
            </button>
            <input
              type="number"
              value={stockQty}
              onChange={(e) => setStockQty(+e.target.value)}
              className="input-field text-center flex-1"
            />
            <button
              onClick={() => setStockQty((q) => q + 10)}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500 text-center">
            Positive = add stock · Negative = reduce stock
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setModal(null)}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button onClick={handleStock} className="btn-primary flex-1">
              Update Stock
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Inventory;
